import { useState, useEffect, useRef, useMemo } from 'react'
import { useTabManager } from '../../../../hooks/useTabManager'
import { useDragAndDrop } from '../../../../hooks/useDragAndDrop'
import { useChatStore } from '../../../../stores/chatStore'
import * as Sentry from '@sentry/react'

import { useChatAgent } from '../useChatAgent'
import { chatPageConfig } from '../../config'
import type { PanelState, PanelActions, PanelViewType } from '../../types/PanelTypes'

interface ControllerProps {
    isOpen: boolean
    onClose: () => void
    onFullscreenChange?: (isFullscreen: boolean) => void
    isFullscreen?: boolean
    agentId?: string | null
    agentName?: string
}

interface ControllerReturn {
    state: PanelState
    actions: PanelActions

    // Shell Components
    tabManager: ReturnType<typeof useTabManager> | null

    // Drag & Drop
    dragHandlers: ReturnType<typeof useDragAndDrop>['handlers']

    // Refs
    refs: {
        searchButton: React.RefObject<HTMLButtonElement | null>
        newTabButton: React.RefObject<HTMLButtonElement | null>
    }
}

/**
 * Industrial Controller Hook for ChatPanelMaster (Shell Isolation)
 * 
 * Manages UI Shell state only. Data for views is requested by views themselves.
 */
export const useChatPanelController = ({
    isOpen,
    onClose,
    onFullscreenChange,
    isFullscreen = false,
    agentId: initialAgentId = null,
    agentName: initialAgentName = '',
}: ControllerProps): ControllerReturn => {
    // ===== VIEW STATE MANAGEMENT =====
    const [view, setView] = useState<PanelViewType>('chat')
    const [isDragging, setIsDragging] = useState(false)

    // ===== TAB MANAGEMENT (SHELL ONLY) =====
    const pageContext = 'global'
    const tabManagerResult = useTabManager(pageContext)
    const { features } = chatPageConfig
    const tabManager = features.tabs ? tabManagerResult : null

    // ===== AGENT NAVIGATION (UI STATE) =====
    const { currentAgentId, currentAgentName } = useChatAgent(
        initialAgentId,
        initialAgentName,
        tabManager
    )

    // ===== REFS =====
    const searchButtonRef = useRef<HTMLButtonElement | null>(null)
    const newTabButtonRef = useRef<HTMLButtonElement | null>(null)

    // ===== TAB INITIALIZATION =====
    const initializedAgentRef = useRef<string | null | 'first_run'>('first_run')
    useEffect(() => {
        if (!features.tabs || !tabManager || tabManager.loading) return
        if (initialAgentId && initializedAgentRef.current !== initialAgentId) {
            tabManager.openTab(initialAgentId, initialAgentName || 'Agente')
            initializedAgentRef.current = initialAgentId
        }
    }, [initialAgentId, initialAgentName, features.tabs, tabManager?.openTab, tabManager?.loading])

    // ===== SENTRY CONTEXT =====
    useEffect(() => {
        if (currentAgentId && currentAgentName) {
            Sentry.setContext('active_agent', {
                id: currentAgentId,
                name: currentAgentName
            })
        } else {
            Sentry.setContext('active_agent', null)
        }
    }, [currentAgentId, currentAgentName])

    // ===== DRAG & DROP =====
    const { handlers: dragHandlers } = useDragAndDrop(async (_files) => {
        // Drag processing handled via ChatContext
    })

    const wrappedDragHandlers = useMemo(() => ({
        onDragEnter: (e: React.DragEvent) => {
            setIsDragging(true)
            dragHandlers.onDragEnter(e)
        },
        onDragLeave: (e: React.DragEvent) => {
            setIsDragging(false)
            dragHandlers.onDragLeave(e)
        },
        onDrop: (e: React.DragEvent) => {
            setIsDragging(false)
            dragHandlers.onDrop(e)
        },
        onDragOver: dragHandlers.onDragOver
    }), [dragHandlers])

    // ===== ACTIONS =====
    const actions: PanelActions = useMemo(() => ({
        closePanel: onClose,
        toggleFullscreen: () => onFullscreenChange?.(!isFullscreen),
        setView: (newView: PanelViewType) => setView(newView),
        goBack: () => setView('chat'),
        openAgentSearch: () => setView('search'),
        startNewChat: () => {
            // Industrial Grade: Explicitly reset domain state for the active agent
            if (currentAgentId) {
                const store = useChatStore.getState()
                store.clearMessages(currentAgentId)
                store.setCurrentConversation(currentAgentId, null)
            }
            setView('chat')
        },
        onTabClick: (tabId: string) => tabManager?.switchTab(tabId),
        onTabClose: (tabId: string) => tabManager?.closeTab(tabId),
    }), [onClose, onFullscreenChange, isFullscreen, tabManager])

    // ===== STATE =====
    const state: PanelState = useMemo(() => ({
        isOpen,
        isFullscreen,
        isDragging,
        view,
        currentAgentId,
        currentAgentName,
        isSyncing: false,
        syncMessage: '',
        canGoBack: view !== 'chat',
        showTabs: features.tabs && (tabManager?.tabs.length ?? 0) > 0,
    }), [
        isOpen,
        isFullscreen,
        isDragging,
        view,
        currentAgentId,
        currentAgentName,
        features.tabs,
        tabManager?.tabs.length
    ])

    return {
        state,
        actions,
        tabManager,
        dragHandlers: wrappedDragHandlers,
        refs: {
            searchButton: searchButtonRef,
            newTabButton: newTabButtonRef
        }
    }
}
